// OrderList.jsx
import React, { useState, useEffect } from "react";
import { PlusCircle, Search, FileText, ArrowUpDown, Edit } from "lucide-react";
import OrderForm from "./OrderForm";
import OrderView from "./OrderView";
import { checkAuth, handleAuthError } from "../../utils/auth";

// Função para formatar CPF ou CNPJ
const formatCpfCnpj = (value) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return value;
};

// Função para formatar valores monetários
const formatCurrency = (value) => {
  if (typeof value !== "number") return "-";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const statusConfig = {
  Orcamento: {
    color: "bg-gray-400",
    title: "Orçamento",
    description: "Orçamento aguardando transformação em Pedido",
  },
  Aguardando: {
    color: "bg-yellow-400",
    title: "Aguardando",
    description: "Pedido em espera para processamento inicial",
  },
  Análise: {
    color: "bg-purple-400",
    title: "Análise",
    description: "Pedido em análise pela equipe técnica",
  },
  Carteira: {
    color: "bg-blue-400",
    title: "Carteira",
    description: "Pedido em carteira para processamento",
  },
  Faturado: {
    color: "bg-green-400",
    title: "Faturado",
    description: "Pedido faturado e finalizado",
  },
  Reprovado: {
    color: "bg-red-400",
    title: "Reprovado",
    description: "Pedido reprovado ou cancelado",
  },
};

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [showStatusLegend, setShowStatusLegend] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado e função para ordenação (sort)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedOrders = (ordersToSort) => {
    if (!sortConfig.key) return ordersToSort;
    return [...ordersToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      aValue = aValue ? aValue.toString().toLowerCase() : "";
      bValue = bValue ? bValue.toString().toLowerCase() : "";
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchOrders = async () => {
    try {
      if (!checkAuth()) {
        window.location.href = "/login";
        return;
      }
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/orders`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      if (!handleAuthError(error)) {
        setError("Erro ao carregar os pedidos. Por favor, tente novamente.");
      }
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter(
    (order) =>
      order.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customerDocument?.includes(search)
  );

  const sortedOrders = getSortedOrders(filteredOrders);

  const StatusDot = ({ status }) => (
    <div
      className={`w-4 h-4 rounded-full ${statusConfig[status].color} border-2 border-white shadow-sm cursor-pointer hover:opacity-80 transition-opacity`}
      onClick={(e) => {
        e.stopPropagation();
        setShowStatusLegend(!showStatusLegend);
      }}
      title="Clique para ver a legenda"
    />
  );

  const StatusLegend = () => (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={() => setShowStatusLegend(false)}
    >
      <div
        className="bg-white rounded-lg p-4 m-4 max-w-sm w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">Status dos Pedidos</h3>
        <div className="space-y-3">
          {Object.entries(statusConfig).map(([key, status]) => (
            <div key={key} className="flex items-center gap-3">
              <div
                className={`w-4 h-4 rounded-full ${status.color} border-2 border-white shadow-sm`}
              />
              <div>
                <p className="font-medium">{status.title}</p>
                <p className="text-sm text-gray-600">{status.description}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 rounded-lg transition-colors"
          onClick={() => setShowStatusLegend(false)}
        >
          Fechar
        </button>
      </div>
    </div>
  );

  // Componente para visualização mobile dos pedidos
  const OrderCard = ({ order }) => {
    const mappedStatus = order.status;
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <StatusDot status={mappedStatus} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 mb-1">Pedido #{order.id}</p>
              <h3 className="font-medium text-gray-900 break-words">
                {order.customerName}
              </h3>
              <p className="text-sm text-gray-500">
                {formatCpfCnpj(order.customerDocument)}
              </p>
            </div>
            <button
              onClick={() => setSelectedOrderId(order.id)}
              className="text-blue-600 hover:text-blue-800 p-1 flex-shrink-0"
              title="Visualizar"
            >
              <FileText className="w-5 h-5" />
            </button>
            {order.status === "Orcamento" && (
              <button
                onClick={() => {
                  setOrderToEdit(order);
                  setIsAddModalOpen(true);
                }}
                className="text-green-600 hover:text-green-800 p-1 flex-shrink-0"
                title="Editar"
              >
                <Edit className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-gray-500">Data</p>
              <p className="font-medium">
                {new Date(order.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Total</p>
              <p className="font-medium">{formatCurrency(order.total)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SortableHeader = ({ label, field }) => (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
      onClick={() => requestSort(field)}
    >
      <div className="flex items-center">
        {label}
        <ArrowUpDown
          className={`w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 ${
            sortConfig.key === field ? "opacity-100" : ""
          }`}
        />
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p className="mb-2">{error}</p>
          <button
            onClick={fetchOrders}
            className="bg-red-100 px-4 py-2 rounded-md hover:bg-red-200 transition-colors w-full"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header com Sticky */}
      <div className="sticky top-0 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl font-semibold text-gray-900">Pedidos</h1>
            <button
              onClick={() => {
                setOrderToEdit(null);
                setIsAddModalOpen(true);
              }}
              className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-red-700 transition-colors w-full sm:w-auto justify-center"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              Novo Pedido
            </button>
          </div>
          {/* Barra de Busca */}
          <div className="mt-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por cliente ou CNPJ ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-shadow bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-4">
          {/* Mobile View */}
          <div className="sm:hidden">
            {sortedOrders.length === 0 ? (
              <div className="bg-white rounded-lg p-4 text-center text-gray-500">
                Nenhum pedido encontrado
              </div>
            ) : (
              sortedOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden sm:block" style={{ marginTop: "0px" }}>
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-8" />
                      <SortableHeader label="Data" field="createdAt" />
                      <SortableHeader label="Pedido" field="id" />
                      <SortableHeader label="Cliente" field="customerName" />
                      <SortableHeader label="CNPJ" field="customerDocument" />
                      <SortableHeader label="Total" field="total" />
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedOrders.length === 0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          Nenhum pedido encontrado
                        </td>
                      </tr>
                    ) : (
                      sortedOrders.map((order) => {
                        const mappedStatus = order.status;
                        return (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="pl-4 py-4">
                              <StatusDot status={mappedStatus} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              { order.createdAt.split("T")[0].split('-').reverse().join('/') }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              #{order.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 break-words">
                              {order.customerName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatCpfCnpj(order.customerDocument)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(order.total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                            {order.status === "Orcamento" && (
                                <button
                                  onClick={() => {
                                    setOrderToEdit(order);
                                    setIsAddModalOpen(true);
                                  }}
                                  className="text-green-600 hover:text-green-800 transition-colors p-1 ml-2"
                                  title="Editar"
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedOrderId(order.id)}
                                className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                                title="Visualizar"
                              >
                                <FileText className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showStatusLegend && <StatusLegend />}

      {isAddModalOpen && (
        <OrderForm
          onClose={() => {
            setIsAddModalOpen(false);
            setOrderToEdit(null);
          }}
          onSave={() => {
            fetchOrders();
            setIsAddModalOpen(false);
            setOrderToEdit(null);
          }}
          existingOrder={orderToEdit}
        />
      )}

      {selectedOrderId && (
        <OrderView
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onEdit={(orderData) => {
            setOrderToEdit(orderData);
            setSelectedOrderId(null);
            setIsAddModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default OrderList;
